import { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { 
  Plus, 
  Wallet, 
  TrendingDown, 
  Calendar, 
  Percent, 
  DollarSign,
  Brain,
  Edit,
  Trash2,
  Home,
  Car,
  GraduationCap,
  CreditCard,
  Loader2,
  Sparkles,
  Clock,
  AlertCircle,
  CheckCircle,
  History,
  BanknoteIcon
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercent = (value) => {
  return `${value.toFixed(2)}%`;
};

const LOAN_TYPE_ICONS = {
  personal: CreditCard,
  heloc: Home,
  auto: Car,
  student: GraduationCap,
  other: Wallet
};

const LOAN_TYPE_COLORS = {
  personal: "#8B5CF6",
  heloc: "#10B981",
  auto: "#F59E0B",
  student: "#3B82F6",
  other: "#6B7280"
};

const LOAN_TYPE_LABELS = {
  personal: "Personal Loan",
  heloc: "HELOC",
  auto: "Auto Loan",
  student: "Student Loan",
  other: "Other"
};

export default function Loans() {
  const { api } = useAuth();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiStrategy, setAiStrategy] = useState(null);
  const [editingLoan, setEditingLoan] = useState(null);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    loan_type: "personal",
    principal_amount: "",
    current_balance: "",
    interest_rate: "",
    monthly_payment: "",
    term_months: "",
    start_date: new Date().toISOString().split('T')[0],
    lender: "",
    notes: ""
  });
  const [paymentData, setPaymentData] = useState({
    amount: "",
    payment_date: new Date().toISOString().split('T')[0],
    notes: "",
    is_extra_payment: false
  });

  const fetchLoans = useCallback(async () => {
    try {
      const res = await api.get("/loans");
      setLoans(res.data);
    } catch (error) {
      console.error("Failed to fetch loans:", error);
      toast.error("Failed to load loans");
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchUpcomingPayments = useCallback(async () => {
    try {
      const res = await api.get("/loans/upcoming-payments");
      setUpcomingPayments(res.data.upcoming_payments || []);
    } catch (error) {
      console.error("Failed to fetch upcoming payments:", error);
    }
  }, [api]);

  useEffect(() => {
    fetchLoans();
    fetchUpcomingPayments();
  }, [fetchLoans, fetchUpcomingPayments]);

  const resetForm = () => {
    setFormData({
      name: "",
      loan_type: "personal",
      principal_amount: "",
      current_balance: "",
      interest_rate: "",
      monthly_payment: "",
      term_months: "",
      start_date: new Date().toISOString().split('T')[0],
      lender: "",
      notes: ""
    });
  };

  const resetPaymentForm = () => {
    setPaymentData({
      amount: "",
      payment_date: new Date().toISOString().split('T')[0],
      notes: "",
      is_extra_payment: false
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        principal_amount: parseFloat(formData.principal_amount),
        current_balance: parseFloat(formData.current_balance),
        interest_rate: parseFloat(formData.interest_rate),
        monthly_payment: parseFloat(formData.monthly_payment),
        term_months: parseInt(formData.term_months)
      };
      
      await api.post("/loans", payload);
      toast.success("Loan added successfully");
      setDialogOpen(false);
      resetForm();
      fetchLoans();
      fetchUpcomingPayments();
    } catch (error) {
      console.error("Failed to add loan:", error);
      toast.error("Failed to add loan");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (loan) => {
    setEditingLoan(loan);
    setFormData({
      name: loan.name,
      loan_type: loan.loan_type,
      principal_amount: loan.principal_amount.toString(),
      current_balance: loan.current_balance.toString(),
      interest_rate: loan.interest_rate.toString(),
      monthly_payment: loan.monthly_payment.toString(),
      term_months: loan.term_months.toString(),
      start_date: loan.start_date,
      lender: loan.lender || "",
      notes: loan.notes || ""
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (submitting || !editingLoan) return;
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        principal_amount: parseFloat(formData.principal_amount),
        current_balance: parseFloat(formData.current_balance),
        interest_rate: parseFloat(formData.interest_rate),
        monthly_payment: parseFloat(formData.monthly_payment),
        term_months: parseInt(formData.term_months)
      };

      await api.put(`/loans/${editingLoan.id}`, payload);
      toast.success("Loan updated successfully");
      setEditDialogOpen(false);
      setEditingLoan(null);
      resetForm();
      fetchLoans();
      fetchUpcomingPayments();
    } catch (error) {
      console.error("Failed to update loan:", error);
      toast.error("Failed to update loan");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (loanId) => {
    if (!window.confirm("Are you sure you want to delete this loan?")) return;

    try {
      await api.delete(`/loans/${loanId}`);
      toast.success("Loan deleted successfully");
      fetchLoans();
      fetchUpcomingPayments();
    } catch (error) {
      console.error("Failed to delete loan:", error);
      toast.error("Failed to delete loan");
    }
  };

  const openPaymentDialog = (loan) => {
    setSelectedLoan(loan);
    setPaymentData({
      amount: loan.monthly_payment.toString(),
      payment_date: new Date().toISOString().split('T')[0],
      notes: "",
      is_extra_payment: false
    });
    setPaymentDialogOpen(true);
  };

  const handleMakePayment = async (e) => {
    e.preventDefault();
    if (submitting || !selectedLoan) return;
    setSubmitting(true);

    try {
      const payload = {
        amount: parseFloat(paymentData.amount),
        payment_date: paymentData.payment_date,
        notes: paymentData.notes || null,
        is_extra_payment: paymentData.is_extra_payment
      };

      await api.post(`/loans/${selectedLoan.id}/payments`, payload);
      toast.success(`Payment of ${formatCurrency(payload.amount)} recorded! Balance updated.`);
      setPaymentDialogOpen(false);
      setSelectedLoan(null);
      resetPaymentForm();
      fetchLoans();
      fetchUpcomingPayments();
    } catch (error) {
      console.error("Failed to record payment:", error);
      toast.error("Failed to record payment");
    } finally {
      setSubmitting(false);
    }
  };

  const openHistoryDialog = async (loan) => {
    setSelectedLoan(loan);
    setHistoryDialogOpen(true);
    
    try {
      const res = await api.get(`/loans/${loan.id}/payments`);
      setPaymentHistory(res.data);
    } catch (error) {
      console.error("Failed to fetch payment history:", error);
      toast.error("Failed to load payment history");
    }
  };

  const getAiStrategy = async () => {
    setAiLoading(true);
    setAiDialogOpen(true);

    try {
      const res = await api.post("/loans/ai-payoff-strategy");
      setAiStrategy(res.data);
    } catch (error) {
      console.error("Failed to get AI strategy:", error);
      toast.error("Failed to get AI payoff strategy");
      setAiDialogOpen(false);
    } finally {
      setAiLoading(false);
    }
  };

  // Calculate summary stats
  const totalBalance = loans.reduce((sum, l) => sum + l.current_balance, 0);
  const totalMonthlyPayment = loans.reduce((sum, l) => sum + l.monthly_payment, 0);
  const avgInterestRate = loans.length > 0 
    ? loans.reduce((sum, l) => sum + l.interest_rate, 0) / loans.length 
    : 0;
  const totalPrincipal = loans.reduce((sum, l) => sum + l.principal_amount, 0);

  // Pie chart data by loan type
  const loansByType = loans.reduce((acc, loan) => {
    const type = loan.loan_type;
    acc[type] = (acc[type] || 0) + loan.current_balance;
    return acc;
  }, {});

  const pieData = Object.entries(loansByType).map(([type, value]) => ({
    name: LOAN_TYPE_LABELS[type],
    value,
    color: LOAN_TYPE_COLORS[type]
  }));

  // Bar chart data for monthly payments
  const barData = loans.map(loan => ({
    name: loan.name.length > 15 ? loan.name.substring(0, 15) + '...' : loan.name,
    payment: loan.monthly_payment,
    balance: loan.current_balance
  }));

  if (loading) {
    return (
      <Layout title="Loans">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Loans & HELOC">
      <div className="space-y-6">
        {/* Upcoming Payment Reminders */}
        {upcomingPayments.length > 0 && (
          <Card className="bg-gradient-to-r from-amber-500/10 to-red-500/10 border-amber-500/30 rounded-none" data-testid="upcoming-payments-card">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                <CardTitle className="text-lg text-white">Upcoming Payment Reminders</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {upcomingPayments.slice(0, 3).map((payment) => (
                  <div 
                    key={payment.loan_id} 
                    className={`p-3 rounded bg-zinc-900/50 border ${payment.is_overdue ? 'border-red-500/50' : payment.days_until_due <= 7 ? 'border-amber-500/50' : 'border-white/10'}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-white text-sm">{payment.loan_name}</p>
                        <p className="text-xs text-zinc-500">{LOAN_TYPE_LABELS[payment.loan_type]}</p>
                      </div>
                      {payment.is_overdue ? (
                        <Badge variant="destructive" className="text-xs">Overdue</Badge>
                      ) : payment.days_until_due <= 7 ? (
                        <Badge className="bg-amber-500/20 text-amber-500 text-xs">Due Soon</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">{payment.days_until_due} days</Badge>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-amber-500 font-semibold">{formatCurrency(payment.amount_due)}</span>
                      <Button 
                        size="sm" 
                        className="h-7 text-xs bg-primary hover:bg-primary/90"
                        onClick={() => {
                          const loan = loans.find(l => l.id === payment.loan_id);
                          if (loan) openPaymentDialog(loan);
                        }}
                        data-testid={`pay-reminder-${payment.loan_id}`}
                      >
                        Pay Now
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <p className="text-zinc-400 text-sm">
            Track personal loans, HELOC, and other debt with AI-powered payoff strategies
          </p>
          <div className="flex gap-3">
            <Button
              onClick={getAiStrategy}
              variant="outline"
              className="border-primary/50 text-primary hover:bg-primary/10"
              disabled={loans.length === 0}
              data-testid="ai-strategy-btn"
            >
              <Brain className="w-4 h-4 mr-2" />
              AI Payoff Strategy
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90" data-testid="add-loan-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Loan
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white">Add New Loan</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Loan Name</Label>
                    <Input
                      placeholder="e.g., Chase Personal Loan"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="loan-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Loan Type</Label>
                    <Select
                      value={formData.loan_type}
                      onValueChange={(value) => setFormData({ ...formData, loan_type: value })}
                    >
                      <SelectTrigger data-testid="loan-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="personal">Personal Loan</SelectItem>
                        <SelectItem value="heloc">HELOC</SelectItem>
                        <SelectItem value="auto">Auto Loan</SelectItem>
                        <SelectItem value="student">Student Loan</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Principal Amount ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="50000"
                        value={formData.principal_amount}
                        onChange={(e) => setFormData({ ...formData, principal_amount: e.target.value })}
                        required
                        data-testid="principal-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Current Balance ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="45000"
                        value={formData.current_balance}
                        onChange={(e) => setFormData({ ...formData, current_balance: e.target.value })}
                        required
                        data-testid="balance-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Interest Rate (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="7.5"
                        value={formData.interest_rate}
                        onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                        required
                        data-testid="interest-rate-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Monthly Payment ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="850"
                        value={formData.monthly_payment}
                        onChange={(e) => setFormData({ ...formData, monthly_payment: e.target.value })}
                        required
                        data-testid="monthly-payment-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Term (months)</Label>
                      <Input
                        type="number"
                        placeholder="60"
                        value={formData.term_months}
                        onChange={(e) => setFormData({ ...formData, term_months: e.target.value })}
                        required
                        data-testid="term-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        required
                        data-testid="start-date-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Lender (optional)</Label>
                    <Input
                      placeholder="e.g., Chase Bank"
                      value={formData.lender}
                      onChange={(e) => setFormData({ ...formData, lender: e.target.value })}
                      data-testid="lender-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Notes (optional)</Label>
                    <Textarea
                      placeholder="Additional notes about this loan..."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      data-testid="notes-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-primary hover:bg-primary/90" 
                    disabled={submitting}
                    data-testid="submit-loan-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      "Add Loan"
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="total-balance-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Total Balance</p>
                  <p className="text-3xl font-bold text-red-500 font-mono">
                    {formatCurrency(totalBalance)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded bg-red-500/20 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-red-500" />
                </div>
              </div>
              <p className="text-sm text-zinc-500 mt-2">{loans.length} active loans</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="monthly-payment-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Monthly Payment</p>
                  <p className="text-3xl font-bold text-amber-500 font-mono">
                    {formatCurrency(totalMonthlyPayment)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded bg-amber-500/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-amber-500" />
                </div>
              </div>
              <p className="text-sm text-zinc-500 mt-2">Combined monthly</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="avg-interest-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Avg Interest Rate</p>
                  <p className="text-3xl font-bold text-violet-500 font-mono">
                    {formatPercent(avgInterestRate)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded bg-violet-500/20 flex items-center justify-center">
                  <Percent className="w-5 h-5 text-violet-500" />
                </div>
              </div>
              <p className="text-sm text-zinc-500 mt-2">Across all loans</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="paid-off-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Paid Off</p>
                  <p className="text-3xl font-bold text-primary font-mono">
                    {formatCurrency(totalPrincipal - totalBalance)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded bg-primary/20 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-primary" />
                </div>
              </div>
              <p className="text-sm text-zinc-500 mt-2">
                {totalPrincipal > 0 ? `${(((totalPrincipal - totalBalance) / totalPrincipal) * 100).toFixed(1)}% of principal` : '0%'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        {loans.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Loan Distribution Pie Chart */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="loan-distribution-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white">Loan Distribution by Type</CardTitle>
              </CardHeader>
              <CardContent>
                {pieData.length > 0 ? (
                  <>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={70}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              background: '#0A0A0A',
                              border: '1px solid rgba(255,255,255,0.1)',
                              borderRadius: '0',
                              fontSize: '12px'
                            }}
                            formatter={(value) => formatCurrency(value)}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2 mt-2">
                      {pieData.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                            <span className="text-zinc-400">{item.name}</span>
                          </div>
                          <span className="font-mono text-white">{formatCurrency(item.value)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="h-48 flex items-center justify-center text-zinc-500">
                    No loan data yet
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Monthly Payments Bar Chart */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="monthly-payments-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white">Monthly Payments by Loan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData}>
                      <XAxis
                        dataKey="name"
                        stroke="#52525B"
                        fontSize={11}
                        tickLine={false}
                        axisLine={{ stroke: '#27272A' }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis
                        stroke="#52525B"
                        fontSize={12}
                        tickLine={false}
                        axisLine={{ stroke: '#27272A' }}
                        tickFormatter={(value) => `$${value}`}
                      />
                      <Tooltip
                        contentStyle={{
                          background: '#0A0A0A',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '0',
                          fontSize: '12px',
                          fontFamily: 'JetBrains Mono'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Bar dataKey="payment" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Loans List */}
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="loans-list">
          <CardHeader>
            <CardTitle className="text-lg text-white">All Loans</CardTitle>
          </CardHeader>
          <CardContent>
            {loans.length === 0 ? (
              <div className="text-center py-12">
                <Wallet className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400 mb-2">No loans added yet</p>
                <p className="text-sm text-zinc-500">Add your first loan to start tracking</p>
              </div>
            ) : (
              <div className="space-y-4">
                {loans.map((loan) => {
                  const Icon = LOAN_TYPE_ICONS[loan.loan_type] || Wallet;
                  const color = LOAN_TYPE_COLORS[loan.loan_type] || "#6B7280";
                  const paidPercent = ((loan.principal_amount - loan.current_balance) / loan.principal_amount) * 100;
                  const isPaidOff = loan.current_balance <= 0;

                  return (
                    <div
                      key={loan.id}
                      className={`p-4 bg-zinc-900/50 border ${isPaidOff ? 'border-primary/50' : 'border-white/5'} hover:border-white/10 transition-all`}
                      data-testid={`loan-item-${loan.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded flex items-center justify-center"
                            style={{ backgroundColor: `${color}20` }}
                          >
                            <Icon className="w-5 h-5" style={{ color }} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-white">{loan.name}</h3>
                              {isPaidOff && (
                                <Badge className="bg-primary/20 text-primary text-xs">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Paid Off
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-zinc-500">
                              {LOAN_TYPE_LABELS[loan.loan_type]} • {loan.lender || 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPaymentDialog(loan)}
                            className="text-primary border-primary/50 hover:bg-primary/10"
                            disabled={isPaidOff}
                            data-testid={`make-payment-${loan.id}`}
                          >
                            <BanknoteIcon className="w-4 h-4 mr-1" />
                            Pay
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openHistoryDialog(loan)}
                            className="text-zinc-400 hover:text-white"
                            data-testid={`history-${loan.id}`}
                          >
                            <History className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(loan)}
                            className="text-zinc-400 hover:text-white"
                            data-testid={`edit-loan-${loan.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(loan.id)}
                            className="text-zinc-400 hover:text-red-500"
                            data-testid={`delete-loan-${loan.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                        <div>
                          <p className="text-xs text-zinc-500 uppercase tracking-wider">Balance</p>
                          <p className={`font-mono font-semibold ${isPaidOff ? 'text-primary' : 'text-red-400'}`}>
                            {formatCurrency(loan.current_balance)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 uppercase tracking-wider">Monthly</p>
                          <p className="font-mono font-semibold text-amber-400">{formatCurrency(loan.monthly_payment)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 uppercase tracking-wider">Interest</p>
                          <p className="font-mono font-semibold text-violet-400">{formatPercent(loan.interest_rate)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 uppercase tracking-wider">Remaining</p>
                          <p className="font-mono font-semibold text-zinc-300">
                            {isPaidOff ? 'Complete!' : `${loan.remaining_months} months`}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-zinc-500">
                          <span>Progress</span>
                          <span>{paidPercent.toFixed(1)}% paid off</span>
                        </div>
                        <Progress value={paidPercent} className="h-2" />
                      </div>

                      {loan.payoff_date && !isPaidOff && (
                        <div className="mt-2 flex items-center gap-2 text-sm text-zinc-400">
                          <Calendar className="w-4 h-4" />
                          <span>Payoff date: {new Date(loan.payoff_date).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Make Payment Dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <BanknoteIcon className="w-5 h-5 text-primary" />
                Make Payment - {selectedLoan?.name}
              </DialogTitle>
            </DialogHeader>
            {selectedLoan && (
              <form onSubmit={handleMakePayment} className="space-y-4">
                <div className="p-3 bg-zinc-900/50 rounded border border-white/5">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Current Balance:</span>
                    <span className="font-mono text-red-400">{formatCurrency(selectedLoan.current_balance)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-zinc-400">Monthly Payment:</span>
                    <span className="font-mono text-amber-400">{formatCurrency(selectedLoan.monthly_payment)}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Payment Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder={selectedLoan.monthly_payment.toString()}
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    required
                    data-testid="payment-amount-input"
                  />
                  <p className="text-xs text-zinc-500">
                    Balance after payment: {formatCurrency(Math.max(0, selectedLoan.current_balance - (parseFloat(paymentData.amount) || 0)))}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Payment Date</Label>
                  <Input
                    type="date"
                    value={paymentData.payment_date}
                    onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                    required
                    data-testid="payment-date-input"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="extra-payment"
                    checked={paymentData.is_extra_payment}
                    onCheckedChange={(checked) => setPaymentData({ ...paymentData, is_extra_payment: checked })}
                  />
                  <label htmlFor="extra-payment" className="text-sm text-zinc-400 cursor-pointer">
                    This is an extra/partial payment (not regular monthly)
                  </label>
                </div>

                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Textarea
                    placeholder="Payment notes..."
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                    data-testid="payment-notes-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-primary hover:bg-primary/90" 
                  disabled={submitting}
                  data-testid="submit-payment-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Record Payment
                    </>
                  )}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Payment History Dialog */}
        <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <History className="w-5 h-5 text-primary" />
                Payment History - {selectedLoan?.name}
              </DialogTitle>
            </DialogHeader>
            {paymentHistory.length === 0 ? (
              <div className="text-center py-8">
                <History className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400">No payments recorded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {paymentHistory.map((payment) => (
                  <div 
                    key={payment.id} 
                    className="p-3 bg-zinc-900/50 rounded border border-white/5"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-mono font-semibold text-primary">{formatCurrency(payment.amount)}</p>
                        <p className="text-xs text-zinc-500">
                          {new Date(payment.payment_date).toLocaleDateString()}
                          {payment.is_extra_payment && (
                            <Badge className="ml-2 text-xs bg-violet-500/20 text-violet-400">Extra Payment</Badge>
                          )}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-zinc-500">Balance After</p>
                        <p className="font-mono text-sm text-zinc-300">{formatCurrency(payment.balance_after)}</p>
                      </div>
                    </div>
                    {payment.notes && (
                      <p className="text-xs text-zinc-400 mt-1">{payment.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Loan</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label>Loan Name</Label>
                <Input
                  placeholder="e.g., Chase Personal Loan"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Loan Type</Label>
                <Select
                  value={formData.loan_type}
                  onValueChange={(value) => setFormData({ ...formData, loan_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal">Personal Loan</SelectItem>
                    <SelectItem value="heloc">HELOC</SelectItem>
                    <SelectItem value="auto">Auto Loan</SelectItem>
                    <SelectItem value="student">Student Loan</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Principal Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.principal_amount}
                    onChange={(e) => setFormData({ ...formData, principal_amount: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Current Balance ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.current_balance}
                    onChange={(e) => setFormData({ ...formData, current_balance: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Interest Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.interest_rate}
                    onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Monthly Payment ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.monthly_payment}
                    onChange={(e) => setFormData({ ...formData, monthly_payment: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Term (months)</Label>
                  <Input
                    type="number"
                    value={formData.term_months}
                    onChange={(e) => setFormData({ ...formData, term_months: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Lender (optional)</Label>
                <Input
                  placeholder="e.g., Chase Bank"
                  value={formData.lender}
                  onChange={(e) => setFormData({ ...formData, lender: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Additional notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90" 
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update Loan"
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* AI Strategy Dialog */}
        <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                AI Payoff Strategy
              </DialogTitle>
            </DialogHeader>
            {aiLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
                <p className="text-zinc-400">Analyzing your loans...</p>
              </div>
            ) : aiStrategy ? (
              <div className="space-y-4">
                {/* Summary */}
                <div className="p-4 bg-zinc-900/50 border border-white/5 rounded">
                  <h3 className="font-semibold text-white mb-2">Summary</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-zinc-500">Total Debt</p>
                      <p className="font-mono text-red-400">{formatCurrency(aiStrategy.summary?.total_debt || 0)}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Monthly Payments</p>
                      <p className="font-mono text-amber-400">{formatCurrency(aiStrategy.summary?.total_monthly_payments || 0)}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Highest Interest</p>
                      <p className="font-mono text-violet-400">{formatPercent(aiStrategy.summary?.highest_interest_rate || 0)}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Projected Savings</p>
                      <p className="font-mono text-primary">{formatCurrency(aiStrategy.summary?.potential_savings || 0)}</p>
                    </div>
                  </div>
                </div>

                {/* Strategy */}
                <div className="p-4 bg-zinc-900/50 border border-white/5 rounded">
                  <h3 className="font-semibold text-white mb-2">Recommended Strategy</h3>
                  <p className="text-zinc-300 whitespace-pre-wrap">{aiStrategy.strategy}</p>
                </div>

                {/* Priority Order */}
                {aiStrategy.priority_order && aiStrategy.priority_order.length > 0 && (
                  <div className="p-4 bg-zinc-900/50 border border-white/5 rounded">
                    <h3 className="font-semibold text-white mb-2">Payoff Priority Order</h3>
                    <ol className="space-y-2">
                      {aiStrategy.priority_order.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-3 text-sm">
                          <span className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold">
                            {idx + 1}
                          </span>
                          <span className="text-zinc-300">{item.name}</span>
                          <span className="text-zinc-500">({formatPercent(item.interest_rate)} APR)</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Tips */}
                {aiStrategy.tips && aiStrategy.tips.length > 0 && (
                  <div className="p-4 bg-zinc-900/50 border border-white/5 rounded">
                    <h3 className="font-semibold text-white mb-2">Money-Saving Tips</h3>
                    <ul className="space-y-2">
                      {aiStrategy.tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                          <span className="text-primary">•</span>
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-zinc-400 text-center py-8">No strategy available</p>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
